import React, { useEffect, useState } from 'react';
import { listPostgresConnections } from '../../utils/api';

/**
 * Config panel for a single postgres_db ingestion group.
 */
const PostgresDbConfig = ({ value = {}, onChange, suffix }) => {
  const pg = value.postgres || {};
  const [connections, setConnections] = useState([]);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!suffix) return;
      try {
        const res = await listPostgresConnections(suffix);
        if (!cancelled) setConnections(res.data || []);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Failed to load connections');
      }
    })();
    return () => { cancelled = true; };
  }, [suffix]);

  const patchPostgres = (patch) => {
    onChange({
      ...(value || {}),
      type: 'postgres_db',
      postgres: { ...pg, ...patch },
    });
  };

  const mappingText = (() => {
    try {
      return JSON.stringify(pg.column_mapping || {}, null, 2);
    } catch {
      return '{}';
    }
  })();

  return (
    <div className="postgres-db-config">
      <div className="form-row">
        <label htmlFor="pg-connection">Postgres connection</label>
        <select
          id="pg-connection"
          className="form-control"
          value={pg.connection_id || ''}
          onChange={(e) => patchPostgres({ connection_id: e.target.value || null })}
        >
          <option value="">Select connection…</option>
          {connections.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {loadError && <div className="hint" style={{ color: '#b91c1c' }}>{loadError}</div>}
        <div className="hint">Create connections via &quot;Ingest from database&quot; wizard.</div>
      </div>

      <div className="form-row">
        <label htmlFor="pg-schema">Schema</label>
        <input
          id="pg-schema"
          className="form-control"
          value={pg.schema || 'public'}
          onChange={(e) => patchPostgres({ schema: e.target.value })}
        />
      </div>

      <div className="form-row">
        <label htmlFor="pg-table">Table</label>
        <input
          id="pg-table"
          className="form-control"
          value={pg.table || ''}
          onChange={(e) => patchPostgres({ table: e.target.value })}
          placeholder="Article"
        />
      </div>

      <div className="form-row">
        <label htmlFor="pg-watermark">Watermark column (delta ingest)</label>
        <input
          id="pg-watermark"
          className="form-control"
          value={pg.watermark_column || ''}
          onChange={(e) => patchPostgres({ watermark_column: e.target.value || null })}
          placeholder="updatedAt"
        />
        <div className="hint">Leave empty for full pull each run (still deduped by unique key).</div>
      </div>

      <div className="form-row">
        <label htmlFor="pg-pks">Primary key columns (comma-separated)</label>
        <input
          id="pg-pks"
          className="form-control"
          value={Array.isArray(pg.primary_key_columns) ? pg.primary_key_columns.join(', ') : ''}
          onChange={(e) => {
            const cols = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
            patchPostgres({ primary_key_columns: cols });
          }}
          placeholder="id"
        />
      </div>

      <div className="form-row">
        <label htmlFor="pg-mapping">Column mapping (JSON: target → source)</label>
        <textarea
          id="pg-mapping"
          className="form-control"
          rows={8}
          value={mappingText}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value || '{}');
              if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                patchPostgres({ column_mapping: parsed });
              }
            } catch (_) { /* allow typing invalid JSON temporarily */ }
          }}
        />
      </div>
    </div>
  );
};

export default PostgresDbConfig;
